import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import { logger } from '../utils/logger';
import { fetchEnrichedUserActivities, EnrichedUserActivity } from '../utils/activityApi';
import { ActivityTable } from './ActivityTable';
import { en } from '../translations/en';

const ACTIVITY_TYPES = [
  'CATALOG_CREATE',
  'CATALOG_UPDATE',
  'CATALOG_DELETE',
  'CATALOG_VISIBILITY_CHANGE',
  'USER_DATA_EXPORT',
];

/**
 * Get the display label for an activity type.
 */
const getActivityLabel = (type: string): string => {
  const labels = en.activityTimeline.activities;
  switch (type) {
    case 'CATALOG_CREATE':
      return labels.catalogCreate;
    case 'CATALOG_UPDATE':
      return labels.catalogUpdate;
    case 'CATALOG_DELETE':
      return labels.catalogDelete;
    case 'CATALOG_VISIBILITY_CHANGE':
      return labels.catalogVisibilityChange;
    case 'USER_DATA_EXPORT':
      return labels.userDataExport;
    default:
      return type;
  }
};

/**
 * ActivityTimeline displays a filterable table of user activities.
 *
 * Uses the enriched activities API to show:
 * - ALI catalog numbers instead of UUIDs
 * - Aggregated rapid updates
 * - Human-readable formatted details
 * - Debounced catalog filter for item-scoped activity views
 */
export const ActivityTimeline: React.FC = () => {
  const [activities, setActivities] = useState<EnrichedUserActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(7);
  const [activityType, setActivityType] = useState<string>('');
  const [catalogFilter, setCatalogFilter] = useState('');
  const [debouncedCatalogFilter, setDebouncedCatalogFilter] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedCatalogFilter(catalogFilter), 500);
    return () => clearTimeout(handler);
  }, [catalogFilter]);

  const loadActivities = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchEnrichedUserActivities(
        days,
        activityType || undefined,
        debouncedCatalogFilter || undefined
      );
      setActivities(response.activities);
      setError(null);
    } catch (err) {
      logger.error('Failed to load activities', err);
      setError(en.activityTimeline.errorLoading);
    } finally {
      setLoading(false);
    }
  }, [days, activityType, debouncedCatalogFilter]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  if (error) {
    return <Alert severity="error" data-testid="activity-error">{error}</Alert>;
  }

  return (
    <Box>
      <Box display="flex" gap={2} mb={3}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{en.activityTimeline.days}</InputLabel>
          <Select
            value={days}
            label={en.activityTimeline.days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <MenuItem value={1}>{en.activityTimeline.timeRanges.oneDay}</MenuItem>
            <MenuItem value={7}>{en.activityTimeline.timeRanges.sevenDays}</MenuItem>
            <MenuItem value={30}>{en.activityTimeline.timeRanges.thirtyDays}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{en.activityTimeline.activityType}</InputLabel>
          <Select
            value={activityType}
            label={en.activityTimeline.activityType}
            onChange={(e) => setActivityType(e.target.value)}
          >
            <MenuItem value="">{en.activityTimeline.allActivities}</MenuItem>
            {ACTIVITY_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {getActivityLabel(type)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label={en.activityTimeline.catalogFilter}
          placeholder={en.activityTimeline.catalogFilterPlaceholder}
          value={catalogFilter}
          onChange={(e) => setCatalogFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        />
      </Box>

      <ActivityTable activities={activities} loading={loading} />
    </Box>
  );
};
